// Item-master attributes overlaid at load, because a base POG's own item
// fields can be stale. Keys are the category's own names; the profile's
// overlayAttr chooses which of them a CDT level reads.
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  RouteGetCategories_categoryId_ProductAttributes,
  ResponseGetCategories_categoryId_ProductAttributes,
  type ProductAttributeValue,
} from "@CommonApi/categories/_categoryId_/product-attributes";
import {
  RouteGetMerchflows_merchflowId_Details,
  ResponseGetMerchflows_merchflowId_Details,
} from "@CommonApi/merchflows/_merchflowId_/details";
import { API, getRequest } from "src/utils/axios";
import type { PogAdapterOptions } from "@CommonUtils/cdtStudio/adapters/pogToStudioItems";
import type { AttributeCandidate } from "../domain/spineInference";

export const STUDIO_ATTRIBUTES_QUERY_ROOT = "cdt-studio-attributes";

export const STUDIO_ATTRIBUTES_QUERY_KEY = (categoryId: number | undefined) =>
  [STUDIO_ATTRIBUTES_QUERY_ROOT, categoryId ?? 0] as const;

export const STUDIO_MERCHFLOW_CATEGORY_QUERY_KEY = (merchflowId: number | undefined) =>
  ["cdt-studio-merchflow-category", merchflowId ?? 0] as const;

const overlayValueOf = (value: ProductAttributeValue): string | null => {
  if (value === null) return null;
  if (typeof value === "string") return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value === "boolean") return value ? "true" : "false";
  return null;
};

const candidateLabelOf = (attr: string): string =>
  attr.replace(/_/g, " ").replace(/\b[a-z]/g, (character) => character.toUpperCase());

export interface StudioAttributesData {
  adapterOptions: PogAdapterOptions;
  preferredOrder: string[];
  /** Echoed off the response, not the request; nullable so a write must refuse rather than guess. */
  categoryId: number | null;
}

export interface StudioAttributesQueryResult {
  data: StudioAttributesData | undefined;
  error: Error | null;
  isError: boolean;
  isLoading: boolean;
}

/** A merchflow with no category is refused, never defaulted to an unscoped universe. */
/** Exported so the consent gate can resolve a category id lazily, without a
 * react-query provider it would otherwise force onto every panel mount. */
export const fetchMerchflowCategoryId = async (merchflowId: number): Promise<number> => {
  const url = `${API}${RouteGetMerchflows_merchflowId_Details.replace(
    ":merchflowId",
    String(merchflowId),
  )}`;
  const { data } = await getRequest<ResponseGetMerchflows_merchflowId_Details>(url);
  if (data.category_id == null) {
    throw new Error(`Merchflow ${merchflowId} has no category_id`);
  }
  return data.category_id;
};

export const fetchStudioAttributes = async (categoryId: number): Promise<StudioAttributesData> => {
  const url = `${API}${RouteGetCategories_categoryId_ProductAttributes.replace(
    ":categoryId",
    String(categoryId),
  )}`;
  const { data } = await getRequest<ResponseGetCategories_categoryId_ProductAttributes>(url);

  const attributeOverlay: Record<string, Record<string, string | null>> = {};
  const nameOverlay: Record<string, string | null> = {};
  const candidateAttrs: string[] = [];
  const seenAttrs = new Set<string>();

  for (const product of data.products) {
    if (!product.productCode) continue;
    if (product.name) nameOverlay[product.productCode] = product.name;
    // Keys are category data, not a CDT contract — pass them through as-is.
    const overlay: Record<string, string | null> = {};
    for (const [attr, value] of Object.entries(product.attributes)) {
      overlay[attr] = overlayValueOf(value);
      if (!seenAttrs.has(attr)) {
        seenAttrs.add(attr);
        candidateAttrs.push(attr);
      }
    }
    attributeOverlay[product.productCode] = overlay;
  }

  const overlayCandidates: AttributeCandidate[] = candidateAttrs.map((attr) => ({
    attr,
    label: candidateLabelOf(attr),
  }));

  return {
    adapterOptions: {
      attributeOverlay,
      overlayCandidates,
      nameOverlay,
    },
    preferredOrder: candidateAttrs,
    categoryId: data.categoryId,
  };
};

/** The item master is mutable (uploads, ETL), so `staleTime` must stay finite. */
export const useStudioAttributesQuery = (
  merchflowId: number | undefined,
  enabled = true,
): StudioAttributesQueryResult => {
  const categoryQuery = useQuery({
    queryKey: STUDIO_MERCHFLOW_CATEGORY_QUERY_KEY(merchflowId),
    enabled: enabled && merchflowId !== undefined,
    staleTime: 60_000,
    queryFn: () => {
      if (merchflowId === undefined) {
        throw new Error("merchflowId is required");
      }
      return fetchMerchflowCategoryId(merchflowId);
    },
  });

  const categoryId = categoryQuery.data;

  const attributesQuery = useQuery({
    queryKey: STUDIO_ATTRIBUTES_QUERY_KEY(categoryId),
    enabled: enabled && categoryId !== undefined,
    staleTime: 60_000,
    queryFn: () => {
      if (categoryId === undefined) {
        throw new Error("categoryId is required");
      }
      return fetchStudioAttributes(categoryId);
    },
  });

  // Fold in the resolution step: while it runs, this query is merely DISABLED.
  return useMemo(
    () => ({
      data: attributesQuery.data,
      isLoading: categoryQuery.isLoading || attributesQuery.isLoading,
      isError: categoryQuery.isError || attributesQuery.isError,
      error: categoryQuery.error ?? attributesQuery.error,
    }),
    [attributesQuery, categoryQuery],
  );
};
